import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx-js-style';
import ExcelJS from 'exceljs';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../utils/dateUtils';

const SeatingAssignmentsPage = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examId, setExamId] = useState('');
  const [groupByRoom, setGroupByRoom] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newPtnNumber, setNewPtnNumber] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [ptnNumber, setPtnNumber] = useState('');
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [nonModular, setNonModular] = useState(false);
  const [levelId, setLevelId] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [emailStatus, setEmailStatus] = useState({});
  const [massEmailStatus, setMassEmailStatus] = useState({sending: false, sent: 0, errors: []});
  const [isStateRestored, setIsStateRestored] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [gdriveUploadStatus, setGdriveUploadStatus] = useState({ uploading: false, message: '' });
  const [filterByUnsentEmails, setFilterByUnsentEmails] = useState(false);
  const [isDownloadingAllFiles, setIsDownloadingAllFiles] = useState(false);
  const BASEURL = process.env.REACT_APP_API_URL


  useEffect(() => {
  const saved = localStorage.getItem('goethe_exam_results');
  if (saved) {
    const parsed = JSON.parse(saved);
    if (parsed.exam_id) {
      setExamId(parsed.exam_id);
      fetchAssignments(parsed.exam_id);

      // Fetch level and then modules
      fetch(`${BASEURL}/exam-level?exam_id=${parsed.exam_id}`)
        .then(res => res.json())
        .then(data => {
          setLevelId(data.level_id);
          return fetch(`${BASEURL}/modules?level=${data.level_id}`);
        })
        .then(res => res.json())
        .then(setModules)
        .catch(err => console.error('Failed to load level/modules:', err));
    }
  }

  // Check if PDF generation was in progress before page refresh
  const pdfGenerationState = localStorage.getItem('pdf_generation_state');
  if (pdfGenerationState) {
    try {
      const parsed = JSON.parse(pdfGenerationState);
      // Check if the generation state is still valid (not older than 30 minutes)
      const isRecent = Date.now() - parsed.timestamp < 30 * 60 * 1000;
      if (parsed.isGenerating && isRecent && parsed.examId) {
        // Only restore state if we have the same examId or if examId is not yet loaded
        if (!examId || parsed.examId === examId) {
          setIsGenerating(true);
          setIsStateRestored(true);
        } else {
          // Clear state if examId doesn't match
          localStorage.removeItem('pdf_generation_state');
        }
      } else {
        // Clear stale generation state
        localStorage.removeItem('pdf_generation_state');
      }
    } catch (error) {
      console.error('Error parsing PDF generation state:', error);
      localStorage.removeItem('pdf_generation_state');
    }
  }
}, []);

// Cleanup effect to handle component unmount
useEffect(() => {
  return () => {
    // If component unmounts while generating, keep the state in localStorage
    // so it can be restored on next visit
    if (isGenerating) {
      const generationState = {
        isGenerating: true,
        examId: examId,
        timestamp: Date.now()
      };
      localStorage.setItem('pdf_generation_state', JSON.stringify(generationState));
    }
  };
}, [isGenerating, examId]);

// Periodic check to clear stale generation state
useEffect(() => {
  const interval = setInterval(() => {
    const pdfGenerationState = localStorage.getItem('pdf_generation_state');
    if (pdfGenerationState) {
      const parsed = JSON.parse(pdfGenerationState);
      const isRecent = Date.now() - parsed.timestamp < 30 * 60 * 1000;
      if (!isRecent && parsed.isGenerating) {
        localStorage.removeItem('pdf_generation_state');
        setIsGenerating(false);
      }
    }
  }, 60000); // Check every minute

  return () => clearInterval(interval);
}, []);

// Update localStorage generation state when examId changes
useEffect(() => {
  if (examId && isGenerating) {
    const pdfGenerationState = localStorage.getItem('pdf_generation_state');
    if (pdfGenerationState) {
      try {
        const parsed = JSON.parse(pdfGenerationState);
        if (parsed.isGenerating && parsed.examId !== examId) {
          // Update the examId in localStorage to match current exam
          const updatedState = { ...parsed, examId };
          localStorage.setItem('pdf_generation_state', JSON.stringify(updatedState));
        }
      } catch (error) {
        console.error('Error updating PDF generation state:', error);
      }
    }
  }
}, [examId, isGenerating]);

// Function to manually reset PDF generation state
const resetPDFGenerationState = () => {
  setIsGenerating(false);
  setIsStateRestored(false);
  localStorage.removeItem('pdf_generation_state');
};

// Function to save PDF locally
const handleSaveLocally = async () => {
  setIsGenerating(true);
  setShowSaveDialog(false);
  
      try {
      // Generate PDF locally
      const response = await fetch(`${BASEURL}/generate-pdf/merge-all?exam_id=${examId}`);
      if (!response.ok) {
        throw new Error('Failed to generate PDFs');
      }
      
      const blob = await response.blob();
      
      // Get exam start date from the first assignment
      const firstAssignment = assignments[0];
      let examDate = '';
      if (firstAssignment && firstAssignment.start_time) {
        try {
          const date = new Date(firstAssignment.start_time);
          if (!isNaN(date.getTime())) { // Check if date is valid
            examDate = formatDateDDMMYYYY(date);
            console.log('Generated filename:', `Exam ${examDate}.pdf`);
          } else {
            console.warn('Invalid date from assignment:', firstAssignment.start_time);
          }
        } catch (error) {
            console.error('Error parsing date:', error);
          }
        }
        
        const filename = examDate ? `Exam ${examDate}.pdf` : `exam_${examId}_students.pdf`;
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error(err);
      alert('Failed to generate PDFs');
    } finally {
      setIsGenerating(false);
    }
};

// Function to save PDF to Google Drive
const handleSaveToGDrive = async () => {
  setIsGenerating(true);
  setShowSaveDialog(false);
  
      try {
      // Generate and upload PDF to Google Drive
      const response = await fetch(`${BASEURL}/generate-pdf/merge-all?exam_id=${examId}`);
      if (!response.ok) {
        throw new Error('Failed to generate PDFs');
      }
      
      const blob = await response.blob();
      
      // Check file size before upload
      const fileSizeMB = blob.size / (1024 * 1024);
      const maxSizeMB = 50; // 50MB limit
      
      if (fileSizeMB > maxSizeMB) {
        setGdriveUploadStatus({ 
          uploading: false, 
          message: `File too large: ${fileSizeMB.toFixed(1)}MB. Maximum allowed: ${maxSizeMB}MB` 
        });
        setIsGenerating(false);
        return;
      }
      
      setGdriveUploadStatus({ uploading: true, message: `Uploading to Google Drive... (${fileSizeMB.toFixed(1)}MB)` });
      
      const formData = new FormData();
      
      // Get exam start date from the first assignment
      const firstAssignment = assignments[0];
      let examDate = '';
      if (firstAssignment && firstAssignment.start_time) {
        try {
          const date = new Date(firstAssignment.start_time);
          if (!isNaN(date.getTime())) { // Check if date is valid
            examDate = formatDateDDMMYYYY(date);
            console.log('Generated filename:', `Exam ${examDate}.pdf`);
          } else {
            console.warn('Invalid date from assignment:', firstAssignment.start_time);
          }
        } catch (error) {
          console.error('Error parsing date:', error);
        }
      }
      
      const filename = examDate ? `Exam ${examDate}.pdf` : `exam_${examId}_students.pdf`;
      
      formData.append('pdf_file', blob, filename);
      formData.append('exam_id', examId);
      formData.append('filename', filename);
      
      const uploadResponse = await fetch(`${BASEURL}/upload-to-gdrive`, {
        method: 'POST',
        body: formData
      });
      
      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Failed to upload to Google Drive');
      }
      
      const result = await uploadResponse.json();
      setGdriveUploadStatus({ uploading: false, message: `Student PDFs uploaded! Now uploading attendance lists...` });
      
      // Now also upload attendance PDFs
      try {
        // Get the list of available attendance PDFs
        const listResponse = await fetch(`${BASEURL}/list-attendance-pdfs?exam_id=${examId}`);
        
        if (listResponse.ok) {
          const pdfData = await listResponse.json();
          const { pdfs } = pdfData;
          
          if (pdfs && pdfs.length > 0) {
            let uploadedCount = 0;
            
            for (const pdf of pdfs) {
              try {
                // Download the PDF (include start_time to get correct time slot)
                const downloadUrl = `${BASEURL}/download-attendance-pdf?exam_id=${examId}&module=${encodeURIComponent(pdf.module)}&room=${encodeURIComponent(pdf.room)}${pdf.start_time ? `&start_time=${encodeURIComponent(pdf.start_time)}` : ''}`;
                const downloadResponse = await fetch(downloadUrl);
                
                if (downloadResponse.ok) {
                  const pdfBlob = await downloadResponse.blob();
                  
                  // Upload to Google Drive
                  const attendanceFormData = new FormData();
                  const gdriveFilename = `${pdf.display_name} Attendance List.pdf`;
                  attendanceFormData.append('pdf_file', pdfBlob, gdriveFilename);
                  attendanceFormData.append('exam_id', examId);
                  attendanceFormData.append('filename', gdriveFilename);
                  
                  const attendanceUploadResponse = await fetch(`${BASEURL}/upload-to-gdrive`, {
                    method: 'POST',
                    body: attendanceFormData
                  });
                  
                  if (attendanceUploadResponse.ok) {
                    uploadedCount++;
                    setGdriveUploadStatus({ 
                      uploading: false, 
                      message: `Uploading attendance lists... (${uploadedCount}/${pdfs.length})` 
                    });
                  }
                }
              } catch (pdfError) {
                console.error(`Error uploading attendance PDF ${pdf.filename}:`, pdfError);
              }
            }
            
            setGdriveUploadStatus({ 
              uploading: false, 
              message: `Successfully uploaded ${uploadedCount + 1} files to Google Drive! (Student PDFs + ${uploadedCount} attendance lists)` 
            });
          } else {
            setGdriveUploadStatus({ uploading: false, message: `Successfully uploaded student PDFs to Google Drive! File ID: ${result.file_id}` });
          }
        } else {
          setGdriveUploadStatus({ uploading: false, message: `Successfully uploaded student PDFs to Google Drive! File ID: ${result.file_id}` });
        }
      } catch (attendanceError) {
        console.error('Error uploading attendance PDFs:', attendanceError);
        setGdriveUploadStatus({ uploading: false, message: `Student PDFs uploaded successfully! Some attendance lists failed to upload.` });
      }
      
      // Show success message for a few seconds
      setTimeout(() => {
        setGdriveUploadStatus({ uploading: false, message: '' });
      }, 8000);
      
    } catch (error) {
      console.error('Google Drive upload error:', error);
      setGdriveUploadStatus({ uploading: false, message: `Upload failed: ${error.message}` });
    } finally {
      setIsGenerating(false);
    }
};

  const fetchAssignments = async (id) => {
    try {
      const res = await fetch(`${BASEURL}/seating-assignments?exam_id=${id}`);
      const data = await res.json();
      setAssignments(data);
    } catch (err) {
      console.error('Failed to load seating assignments', err);
    } finally {
      setLoading(false);
    }
  };

  const convertToExcel = (rows) => {
    // Group assignments by student
    const studentMap = new Map();
    
    rows.forEach((assignment) => {
      const studentKey = assignment.student_id;
      if (!studentMap.has(studentKey)) {
        studentMap.set(studentKey, {
          ptn_number: assignment.ptn_number || '-',
          last_name: assignment.last_name || '',
          first_name: assignment.first_name || '',
          date_of_birth: assignment.date_of_birth || '',
          city_of_birth: assignment.city_of_birth || '',
          email: assignment.email || '',
          level: assignment.level || '',
          exam_date: formatDateDDMMYYYY(assignment.start_time),
          modules: {},
          preparation_times: {},
          rooms: {}
        });
      }
      
      // Store module start time, preparation time, and room
      const student = studentMap.get(studentKey);
      const isA1A2Mundlich = (assignment.level === 'A1' || assignment.level === 'A2') && assignment.module === 'Mundlich';
      
      // For A1/A2 Mundlich, show preparation time instead of start time in the main column
      if (isA1A2Mundlich && assignment.preparation_time) {
        student.modules[assignment.module] = new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        });
      } else {
        student.modules[assignment.module] = new Date(assignment.start_time).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        });
      }
      
      // Store preparation time for non-A1/A2 Mundlich levels (to show prep → start format)
      if (assignment.preparation_time && !isA1A2Mundlich) {
        student.preparation_times[assignment.module] = new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        });
      }
      
      // Store room information for this module
      student.rooms[assignment.module] = assignment.room || '';
    });
    
    // Dynamically determine which modules are present in the data
    const availableModules = new Set();
    rows.forEach((assignment) => {
      if (assignment.module) {
        availableModules.add(assignment.module);
      }
    });
    
    // Define preferred module order - use standard modules first, then A1/A2 modules
    const standardModules = ['Lesen', 'Horen', 'Schreiben', 'Sprechen'];
    const a1a2Modules = ['Schriftlich', 'Mundlich'];
    
    // Create ordered list of modules that are actually present
    const moduleOrder = [];
    // First add standard modules that are present
    standardModules.forEach(module => {
      if (availableModules.has(module)) {
        moduleOrder.push(module);
      }
    });
    // Then add A1/A2 modules that are present
    a1a2Modules.forEach(module => {
      if (availableModules.has(module)) {
        moduleOrder.push(module);
      }
    });
    // Add any other modules that weren't in our predefined lists
    availableModules.forEach(module => {
      if (!moduleOrder.includes(module)) {
        moduleOrder.push(module);
      }
    });
    
    // Create header with both time and room columns for each module
    const header = ['PTN_Nummer', 'Nachname', 'Name', 'Geburtsdatum', 'Geburtsort', 'Email', 'Niveau'];
    moduleOrder.forEach(module => {
      header.push(module);
      header.push(`${module} Raum`);
    });
    header.push('Prüfungsdatum');
    
    // Create body rows
    const body = Array.from(studentMap.values()).map((student) => {
      const row = [
        student.ptn_number,
        student.last_name,
        student.first_name,
        student.date_of_birth,
        student.city_of_birth,
        student.email,
        student.level
      ];
      
      // Add module times and rooms in order, empty string if module doesn't exist
      moduleOrder.forEach(module => {
        const moduleTime = student.modules[module] || '';
        const prepTime = student.preparation_times[module] || '';
        const moduleRoom = student.rooms[module] || '';
        
        // Add time column - if there's preparation time, show both times
        if (prepTime) {
          row.push(`${prepTime} → ${moduleTime}`);
        } else {
          row.push(moduleTime);
        }
        
        // Add room column
        row.push(moduleRoom);
      });
      
      // Add exam date at the end
      row.push(student.exam_date);
      
      return row;
    });
    
    return [header, ...body];
  };

  const handleUpdatePtnNumber = async (studentId, assignmentId) => {
  try {
    const res = await fetch(`${BASEURL}/update-ptn-number/${studentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ptn_number: newPtnNumber })
    });

    const result = await res.json();
    if (result.message) {
      // Update local state
      setAssignments(prev => prev.map(a =>
        a.assignment_id === assignmentId
          ? { ...a, ptn_number: newPtnNumber }
          : a
      ));
      setEditingId(null);
    }
  } catch (err) {
    console.error('Failed to update PTN Number:', err);
  }
};

const handleAddStudent = async () => {
  try {
    const res = await fetch(`${BASEURL}/dd-student-to-exam`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        exam_id: examId,
        level_id: levelId,
        first_name: firstName,
        last_name: lastName,
        ptn_number: ptnNumber,
        module_id: nonModular ? null : selectedModule,
        non_modular: nonModular
      })
    });

    const data = await res.json();
    if (data.message) {
      alert(data.message);
      setShowDialog(false);
      setFirstName('');
      setLastName('');
      setPtnNumber('');
      setSelectedModule('');
      fetchAssignments(examId); // refresh table
    } else {
      alert(data.error || 'Something went wrong');
    }
  } catch (err) {
    console.error(err);
    alert('Failed to add student');
  }
};



  const downloadExcel = (data, filename) => {
    const worksheet = XLSX.utils.aoa_to_sheet(convertToExcel(data));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Master List');
    
    // Auto-size columns for new format: PTN_Nummer, Nachname, Name, Geburtsdatum, Geburtsort, Email, Level,
    // Lesen, Lesen Raum, Horen, Horen Raum, Schreiben, Schreiben Raum, Sprechen, Sprechen Raum, Prüfungsdatum
    const max_last_name_width = data.reduce((w, r) => Math.max(w, (r.last_name || '').length), 10);
    const max_first_name_width = data.reduce((w, r) => Math.max(w, (r.first_name || '').length), 10);
    const max_city_width = data.reduce((w, r) => Math.max(w, (r.city_of_birth || '').length), 10);
    const max_email_width = data.reduce((w, r) => Math.max(w, (r.email || '').length), 10);
    
    worksheet['!cols'] = [
      { wch: 15 }, // PTN_Nummer
      { wch: Math.max(max_last_name_width, 15) }, // Nachname
      { wch: Math.max(max_first_name_width, 15) }, // Name
      { wch: 15 }, // Geburtsdatum
      { wch: Math.max(max_city_width, 15) }, // Geburtsort
      { wch: Math.max(max_email_width, 25) }, // Email
      { wch: 12 }, // Level
      { wch: 12 }, // Lesen
      { wch: 12 }, // Lesen Raum
      { wch: 12 }, // Horen
      { wch: 12 }, // Horen Raum
      { wch: 12 }, // Schreiben
      { wch: 12 }, // Schreiben Raum
      { wch: 12 }, // Sprechen
      { wch: 12 }, // Sprechen Raum
      { wch: 15 }  // Prüfungsdatum
    ];
    
    // Apply Verdana font to all cells
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;
        if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
        worksheet[cellAddress].s.font = { name: 'Verdana' };
      }
    }
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.replace('.csv', '.xlsx');
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadAll = () => {
  const examName = assignments.length > 0 && assignments[0].exam_name ? 
    `_${assignments[0].exam_name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
  downloadExcel(filteredAssignments, `master_list${examName}.xlsx`);
};

const handleDownloadAllExamFiles = async () => {
  // For "Download All Exam Files", go directly to Google Drive upload
  setIsDownloadingAllFiles(true);
  await uploadAllExamFilesToGDrive();
};



const uploadAllExamFilesToGDrive = async () => {
  try {
    setGdriveUploadStatus({ uploading: true, message: 'Creating exam folder and uploading files to Google Drive...' });
    
    // Get exam name for folder
    const examName = assignments.length > 0 && assignments[0].exam_name ? 
      assignments[0].exam_name.replace(/[^a-zA-Z0-9]/g, '_') : `exam_${examId}`;
    
    // Create form data with all files
    const formData = new FormData();
    formData.append('exam_id', examId);
    formData.append('exam_name', examName);
    formData.append('action', 'upload_all_exam_files');
    
    const uploadResponse = await fetch(`${BASEURL}/upload-all-exam-files-to-gdrive`, {
      method: 'POST',
      body: formData
    });
    
    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json();
      throw new Error(errorData.error || 'Failed to upload exam files to Google Drive');
    }
    
    const result = await uploadResponse.json();
    setGdriveUploadStatus({ 
      uploading: false, 
      message: `Successfully uploaded all exam files to Google Drive! Folder: ${result.folder_name}` 
    });
    
    // Show success message for a few seconds
    setTimeout(() => {
      setGdriveUploadStatus({ uploading: false, message: '' });
    }, 5000);
    
  } catch (error) {
    console.error('Google Drive upload error:', error);
    setGdriveUploadStatus({ uploading: false, message: `Upload failed: ${error.message}` });
  }
};

const handleGeneratePDF = (studentId) => {
  fetch(`${BASEURL}/generate-pdf/${studentId}`)
    .then(res => {
      if (!res.ok) throw new Error('Failed to generate PDF');
      return res.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exam_${studentId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    })
    .catch(err => {
      console.error(err);
      alert('Failed to generate PDF');
    });
};

const handleSendEmail = (studentId) => {
  setEmailStatus((prev) => ({ ...prev, [studentId]: 'sending' }));
  fetch(`${BASEURL}/send-invitation/${studentId}`, {
    method: 'POST',
  })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        setEmailStatus((prev) => ({ ...prev, [studentId]: 'sent' }));
      } else {
        setEmailStatus((prev) => ({ ...prev, [studentId]: 'error' }));
        alert(data.error || 'Failed to send email');
      }
    })
    .catch(err => {
      setEmailStatus((prev) => ({ ...prev, [studentId]: 'error' }));
      alert('Failed to send email');
    });
};

const [progress, setProgress] = useState({
  current: 0,
  total: 0,
  message: '',
  isGenerating: false,
  downloadUrl: null,
  errors: []
});

const [asyncTaskId, setAsyncTaskId] = useState(null);
const [asyncProgress, setAsyncProgress] = useState(0);
const [asyncStatus, setAsyncStatus] = useState(null);

const handleGenerateAllPDFs = async () => {
  if (!examId) {
    alert('No exam selected');
    return;
  }

  try {
    setIsGenerating(true);
    setAsyncProgress(0);
    setAsyncStatus('starting');

    // Call new async endpoint
    const response = await fetch(`${BASEURL}/generate-pdf/merge-all-async`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        exam_id: examId,
        user_email: null // Optional: can add user email for notification
      })
    });

    if (!response.ok) {
      throw new Error('Failed to start PDF generation');
    }

    const data = await response.json();
    const taskId = data.task_id;
    setAsyncTaskId(taskId);
    setAsyncStatus('processing');

    // Start polling for progress
    pollAsyncProgress(taskId);

  } catch (error) {
    console.error('Error starting async PDF generation:', error);
    alert('Failed to start PDF generation: ' + error.message);
    setIsGenerating(false);
    setAsyncStatus('failed');
  }
};

const pollAsyncProgress = async (taskId) => {
  try {
    const response = await fetch(`${BASEURL}/pdf-generation-progress/${taskId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch progress');
    }

    const task = await response.json();
    
    // Update progress
    setAsyncProgress(task.progress || 0);
    setAsyncStatus(task.status);

    if (task.status === 'completed') {
      // PDF is ready!
      setIsGenerating(false);
      setAsyncStatus('completed');
      
      // Automatically download the PDF
      const downloadUrl = `${BASEURL}/download-async-pdf/${taskId}`;
      window.open(downloadUrl, '_blank');
      
      alert(`PDF generation complete! ${task.total_students} students processed.`);
      
    } else if (task.status === 'failed') {
      setIsGenerating(false);
      setAsyncStatus('failed');
      alert('PDF generation failed: ' + (task.error_message || 'Unknown error'));
      
    } else {
      // Still processing, poll again in 2 seconds
      setTimeout(() => pollAsyncProgress(taskId), 2000);
    }

  } catch (error) {
    console.error('Error polling progress:', error);
    // Retry in 3 seconds
    setTimeout(() => pollAsyncProgress(taskId), 3000);
  }
};

const Spinner = () => (
  <div style={{
    display: 'inline-block',
    width: '20px',
    height: '20px',
    border: '3px solid rgba(0,0,0,.3)',
    borderRadius: '50%',
    borderTopColor: '#000',
    animation: 'spin 1s ease-in-out infinite',
    marginRight: '8px'
  }} />
);

  const downloadRoomExcel = (data, filename) => {
    // Create header for room-specific format
    const header = ['Student', 'PTN Number', 'Module', 'Start Time', 'Preparation Time', 'Room'];
    
    // Create body rows with student info, module, time, preparation time, and room
    const body = data.map((assignment) => [
      `${assignment.first_name} ${assignment.last_name}`,
      assignment.ptn_number || '-',
      assignment.module,
      new Date(assignment.start_time).toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      }),
      assignment.preparation_time ? new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      }) : '-',
      assignment.room
    ]);
    
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Room Assignments');
    
    // Auto-size columns for room format
    const max_student_width = data.reduce((w, r) => Math.max(w, `${r.first_name} ${r.last_name}`.length), 10);
    worksheet['!cols'] = [
      { wch: Math.max(max_student_width, 20) }, // Student name
      { wch: 15 }, // PTN Number
      { wch: 15 }, // Module
      { wch: 15 }, // Start Time
      { wch: 20 }, // Preparation Time
      { wch: 15 }  // Room
    ];
    
    // Apply Verdana font to all cells
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;
        if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
        worksheet[cellAddress].s.font = { name: 'Verdana' };
      }
    }
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPerRoom = () => {
    const examName = assignments.length > 0 && assignments[0].exam_name ? 
      `_${assignments[0].exam_name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
    
    const grouped = assignments.reduce((acc, item) => {
      if (!acc[item.room]) acc[item.room] = [];
      acc[item.room].push(item);
      return acc;
    }, {});
    Object.entries(grouped).forEach(([room, rows]) => {
      downloadRoomExcel(rows, `seating_assignments_${room}${examName}.xlsx`);
    });
  };

  const downloadModuleExcel = (data, filename) => {
    // Check if this is a Sprechen/Mundlich level - always use the detailed format for these modules
    const isSprechenLevel = data.some(assignment => assignment.module === 'Sprechen' || assignment.module === 'Mundlich');
    
    if (isSprechenLevel) {
      // Create header for Sprechen/Mundlich format with the exact order specified
      const header = [
        'Preparation Time',  // Preparation start time
        'Start Time',        // Exam start time
        'Room',              // Room assignment
        'PTN Number',        // Student PTN number
        'Last Name',         // Last name
        'First Name',        // First name
        'Date of Birth',     // Date of birth
        'City of Birth',     // Place of birth
        'Niveau'              // Exam level
      ];
      
      // Create body rows with proper timing format in the specified order
      const body = data.map((assignment) => {
        // Check if this is A1/A2 Mundlich - if so, apply the same logic as handleDownloadSprechenLevel
        const isA1A2Mundlich = (assignment.level === 'A1' || assignment.level === 'A2') && assignment.module === 'Mundlich';
        
        return [
          // Preparation time column - only show for non-A1/A2 Mundlich levels
          (!isA1A2Mundlich && assignment.preparation_time) ? new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit'
          }) : '',
          // Start time column - for A1/A2 Mundlich show preparation time, otherwise show start time
          isA1A2Mundlich && assignment.preparation_time ? 
            new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit'
            }) : 
            new Date(assignment.start_time).toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit'
            }),
          assignment.room,
          assignment.ptn_number || '',
          assignment.last_name || '',
          assignment.first_name || '',
          assignment.date_of_birth || '',
          assignment.city_of_birth || '',
          assignment.level || 'N/A' // Use actual level from data
        ];
      });
      
      const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Speaking Assignments');
      
      // Apply Verdana font to all cells
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!worksheet[cellAddress]) continue;
          if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
          worksheet[cellAddress].s.font = { name: 'Verdana' };
        }
      }
      
      // Auto-size columns for Sprechen/Mundlich format
      worksheet['!cols'] = [
        { wch: 20 }, // Preparation Time
        { wch: 15 }, // Start Time
        { wch: 15 }, // Room
        { wch: 15 }, // PTN Number
        { wch: 20 }, // Last Name
        { wch: 20 }, // First Name
        { wch: 15 }, // Date of Birth
        { wch: 20 }, // City of Birth
        { wch: 10 }  // Level
      ];
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      // Create header for standard module format
      const header = ['Student', 'PTN Number', 'Module', 'Start Time', 'Room'];
      
      // Create body rows with student info, module, time, and room
      const body = data.map((assignment) => [
        `${assignment.first_name} ${assignment.last_name}`,
        assignment.ptn_number || '-',
        assignment.module,
        new Date(assignment.start_time).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        }),
        assignment.room
      ]);
      
      const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Module Assignments');
      
      // Apply Verdana font to all cells
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!worksheet[cellAddress]) continue;
          if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
          worksheet[cellAddress].s.font = { name: 'Verdana' };
        }
      }
      
      // Auto-size columns for module format
      const max_student_width = data.reduce((w, r) => Math.max(w, `${r.first_name} ${r.last_name}`.length), 10);
      worksheet['!cols'] = [
        { wch: Math.max(max_student_width, 20) }, // Student name
        { wch: 15 }, // PTN Number
        { wch: 15 }, // Module
        { wch: 15 }, // Start Time
        { wch: 15 }  // Room
      ];
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadPerModule = () => {
    const examName = assignments.length > 0 && assignments[0].exam_name ? 
      `_${assignments[0].exam_name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
    
    // Get the level from the first assignment
    const level = assignments.length > 0 ? assignments[0].level || 'Unknown' : 'Unknown';
    
    // Group assignments by module AND room
    const grouped = assignments.reduce((acc, item) => {
      const key = `${item.module}|${item.room}`; // Use | as separator to avoid conflicts with room names containing hyphens
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
    
    // Download separate Excel file for each module-room combination
    Object.entries(grouped).forEach(([moduleRoomKey, rows]) => {
      const [module, room] = moduleRoomKey.split('|');
      
      // Check if this is a Sprechen module with preparation time
      const hasPreparationTime = rows.some(assignment => assignment.preparation_time);
      const isSprechenLevel = module === 'Sprechen' || module === 'Mundlich';
      
      // Create filename in the format: LEVEL-MODULE-ROOM
      const cleanRoom = room.replace(/[^a-zA-Z0-9]/g, '_');
      let filename;
      if (hasPreparationTime && isSprechenLevel) {
        // Special filename for Sprechen level with preparation time
        filename = `${level}-${module}-${cleanRoom}${examName}.xlsx`;
      } else {
        // Standard filename for other modules
        filename = `${level}-${module}-${cleanRoom}${examName}.xlsx`;
      }
      
      downloadModuleExcel(rows, filename);
    });
  };

  const handleDownloadSprechenLevel = async () => {
    const examName = assignments.length > 0 && assignments[0].exam_name ? 
      `_${assignments[0].exam_name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
    
    const sprechenAssignments = assignments.filter(a => a.module === 'Sprechen' || a.module === 'Mundlich');
    if (sprechenAssignments.length === 0) {
      alert('No Sprechen/Mundlich assignments found to download.');
      return;
    }

    // Get unique rooms and assign colors
    const uniqueRooms = [...new Set(sprechenAssignments.map(a => a.room))];
    const roomColors = [
      'CCEEFF', // Light blue
      'CCFFCC', // Light green
      'FFFFCC', // Light yellow
      'FFCCFF', // Light pink
      'FFE6CC', // Light orange
      'E6CCFF', // Light purple
      'CCFFFF', // Light cyan
      'FFD9CC', // Light coral
      'E6F2FF', // Light sky blue
      'F0E6FF'  // Light lavender
    ];
    
    // Create a map of room to color
    const roomColorMap = {};
    uniqueRooms.forEach((room, index) => {
      roomColorMap[room] = roomColors[index % roomColors.length];
    });

    // Create header for Sprechen level format with the exact order specified
    const header = [
      'Vorbereitungszeit',  // Preparation start time
      'Startzeit',        // Exam start time
      'Raum',              // Room assignment
      'PTN-Nummer',        // Student PTN number
      'Nachname',         // Last name
      'Vorname',        // First name
      'Geburtsdatum',     // Date of birth
      'Geburtsort',     // Place of birth
      'Niveau',            // Exam level
      'Ort',               // Location (hardcoded)
      'Runde'              // Round number
    ];
    
    // Group assignments by preparation time to determine rounds
    const timeGroups = [];
    let currentTime = null;
    let currentGroup = [];
    
    sprechenAssignments.forEach((assignment, index) => {
      const isA1A2Mundlich = (assignment.level === 'A1' || assignment.level === 'A2') && assignment.module === 'Mundlich';
      
      // For Level A: group by Startzeit column (column B), which shows preparation_time
      // For other levels: group by Vorbereitungszeit column (column A)
      let displayTime = '';
      if (isA1A2Mundlich && assignment.preparation_time) {
        // Level A: use preparation_time (shown in Startzeit column B)
        displayTime = new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit'
        });
      } else if (!isA1A2Mundlich && assignment.preparation_time) {
        // Other levels: use preparation_time (shown in Vorbereitungszeit column A)
        displayTime = new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit'
        });
      }
      
      if (displayTime !== currentTime) {
        if (currentGroup.length > 0) {
          timeGroups.push(currentGroup);
        }
        currentTime = displayTime;
        currentGroup = [index];
      } else {
        currentGroup.push(index);
      }
    });
    
    // Push the last group
    if (currentGroup.length > 0) {
      timeGroups.push(currentGroup);
    }
    
    // Create a map of index to round number (only for last row in each group)
    const roundMap = {};
    timeGroups.forEach((group, roundIndex) => {
      const lastIndexInGroup = group[group.length - 1];
      roundMap[lastIndexInGroup] = `Runde ${roundIndex + 1}`;
    });
    
    // Create body rows with proper timing format in the specified order
    const body = sprechenAssignments.map((assignment, index) => {
      // Check if this is A1/A2 Mundlich - if so, swap the time columns
      const isA1A2Mundlich = (assignment.level === 'A1' || assignment.level === 'A2') && assignment.module === 'Mundlich';
      
      return [
        // Preparation time column - only show for non-A1/A2 Mundlich levels
        (!isA1A2Mundlich && assignment.preparation_time) ? new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit'
        }) : '',
        // Start time column - for A1/A2 Mundlich show preparation time, otherwise show start time
        isA1A2Mundlich && assignment.preparation_time ? 
          new Date(assignment.preparation_time).toLocaleTimeString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit'
          }) : 
          new Date(assignment.start_time).toLocaleTimeString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit'
          }),
        assignment.room,
        assignment.ptn_number || '',
        assignment.last_name || '',
        assignment.first_name || '',
        assignment.date_of_birth || '',
        assignment.city_of_birth || '',
        assignment.level || 'N/A', // Use actual level from data
        'GZ-Prishtina', // Hardcoded location
        roundMap[index] || '' // Round number (only on last row of each time group)
      ];
    });
    
    // Get exam level and date from first assignment
    const firstAssignment = sprechenAssignments[0];
    const examLevel = firstAssignment.level || 'N/A';
    const examDate = firstAssignment.start_time ? formatDateDDMMYYYY(firstAssignment.start_time) : '';
    
    // Check if level is A (A1 or A2) - if so, exclude Vorbereitungszeit column
    const isLevelA = examLevel.startsWith('A');
    
    // Conditionally create header and body based on level
    const finalHeader = isLevelA ? header.slice(1) : header; // Remove first column if level A
    const finalBody = isLevelA ? body.map(row => row.slice(1)) : body; // Remove first column if level A
    
    // Create ExcelJS workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sprechen Assignments');
    
    // Add logo image from backend
    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const logoUrl = `${BASE_URL}/logo`;
    try {
      console.log('Attempting to fetch logo from:', logoUrl);
      const response = await fetch(logoUrl, {
        mode: 'cors',
        cache: 'no-cache'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log('Logo blob received:', blob.type, blob.size);
      const buffer = await blob.arrayBuffer();
      
      const imageId = workbook.addImage({
        buffer: buffer,
        extension: 'jpeg',
      });
      
      console.log('Image ID created:', imageId);
      
      // Add image to worksheet (top right, columns H-J, rows 1-3)
      worksheet.addImage(imageId, {
        tl: { col: 7, row: 2 }, // Top-left: column H, row 1
        ext: { width: 182, height: 85 } // Width and height in pixels
      });
      
      console.log('Logo successfully added to worksheet');
    } catch (error) {
      console.error('Failed to load logo:', error);
      console.error('Error details:', error.message);
      // Continue without logo if it fails
    }
    
    // Create empty row with appropriate number of columns based on level
    const numColumns = isLevelA ? 10 : 11;
    const emptyRow = Array(numColumns).fill('');
    
    // Row 1: Empty
    worksheet.addRow(emptyRow);
    
    // Row 2: Empty
    worksheet.addRow(emptyRow);
    
    // Row 3: Empty
    worksheet.addRow(emptyRow);
    
    // Row 4: Level and Date with green background (merged C4:F4 for level A, or C4:F4 for other levels)
    worksheet.addRow(emptyRow);
    worksheet.mergeCells('C4:F4');
    
    // Apply formatting and text to the merged cell
    const mergedCell = worksheet.getCell('C4');
    mergedCell.value = `${examLevel}    ${examDate}`;
    mergedCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF92D050' }
    };
    mergedCell.font = { bold: true, size: 14, name: 'Verdana' };
    mergedCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Set row height
    worksheet.getRow(4).height = 25;
    
    // Row 5: Empty
    worksheet.addRow(emptyRow);
    
    // Row 6: Field labels with box borders for writing
    const fieldsRow = worksheet.addRow(isLevelA ? ['Prüfer:', '', '', 'Datum:', '', 'Raum:', '', 'Schichten:', '', 'PTN-s:'] : ['Prüfer:', '', '', 'Datum:', '', 'Raum:', '', 'Schichten:', '', 'PTN-s:', '']);
    fieldsRow.font = { name: 'Verdana' };
    // Add box borders around each label and its adjacent cell(s)
    fieldsRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const borderStyle = { style: 'thin', color: { argb: 'FF000000' } };
      
      // Prüfer: box (columns 1-2)
      if (colNumber === 1) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          left: borderStyle
        };
        cell.font = { name: 'Verdana', bold: true };
      } else if (colNumber === 2) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          right: borderStyle
        };
      }
      // Datum: box (columns 4-5)
      else if (colNumber === 4) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          left: borderStyle
        };
        cell.font = { name: 'Verdana', bold: true };
      } else if (colNumber === 5) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          right: borderStyle
        };
      }
      // Raum: box (columns 6-7)
      else if (colNumber === 6) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          left: borderStyle
        };
        cell.font = { name: 'Verdana', bold: true };
      } else if (colNumber === 7) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          right: borderStyle
        };
      }
      // Schichten: box (columns 8-9)
      else if (colNumber === 8) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          left: borderStyle
        };
        cell.font = { name: 'Verdana', bold: true };
      } else if (colNumber === 9) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          right: borderStyle
        };
      }
      // PTN-s: box (column 10)
      else if (colNumber === 10) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          left: borderStyle,
          right: borderStyle
        };
        cell.font = { name: 'Verdana', bold: true };
      }
    });
    
    // Row 7: Empty values
    worksheet.addRow(emptyRow);
    
    // Row 8: Unterschrift with box border
    const unterschriftRow = worksheet.addRow(isLevelA ? ['Unterschrift:', '', '', '', '', '', '', '', '', ''] : ['Unterschrift:', '', '', '', '', '', '', '', '', '', '']);
    unterschriftRow.font = { name: 'Verdana' };
    // Add box border around Unterschrift label and cells for signature
    unterschriftRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const borderStyle = { style: 'thin', color: { argb: 'FF000000' } };
      
      // Unterschrift: box (columns 1-2)
      if (colNumber === 1) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          left: borderStyle
        };
        cell.font = { name: 'Verdana', bold: true };
      } else if (colNumber === 2) {
        cell.border = {
          top: borderStyle,
          bottom: borderStyle,
          right: borderStyle
        };
      }
    });
    
    // Row 9-10: Empty separators
    worksheet.addRow(emptyRow);
    worksheet.addRow(emptyRow);
    
    // Row 11: Column headers (use finalHeader which excludes Vorbereitungszeit for level A)
    const headerRow = worksheet.addRow(finalHeader);
    headerRow.font = { bold: true, name: 'Verdana' };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Apply green background to all header cells
    headerRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF92D050' }
      };
    });
    
    // Add data rows with colors (use finalBody which excludes first column for level A)
    finalBody.forEach((rowData, index) => {
      const room = sprechenAssignments[index]?.room;
      const bgColor = roomColorMap[room];
      const dataRow = worksheet.addRow(rowData);
      
      // Check if this row has a "Runde" value (last column)
      const rundeValue = rowData[rowData.length - 1]; // Last element is Runde
      const hasRunde = rundeValue && rundeValue.toString().trim() !== '';
      
      // Apply room color, Verdana font, and bottom border if Runde exists
      dataRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: `FF${bgColor}` }
        };
        cell.font = { name: 'Verdana' };
        
        // Add bottom border if this row has a Runde value
        if (hasRunde) {
          cell.border = {
            ...cell.border,
            bottom: { style: 'thin', color: { argb: 'FF000000' } }
          };
        }
      });
    });
    
    // Add footer section
    const footerStartRow = worksheet.rowCount + 3;
    worksheet.addRow(emptyRow);
    worksheet.addRow(emptyRow);
    
    // Kommentare
    const kommentareRow = worksheet.addRow([...Array(numColumns).fill('')].map((v, i) => i === 0 ? 'Kommentare:' : ''));
    kommentareRow.getCell(1).font = { bold: true, name: 'Verdana' };
    
    // Empty rows for comments
    for (let i = 0; i < 5; i++) {
      worksheet.addRow(emptyRow);
    }
    
    worksheet.addRow(emptyRow);
    
    // Add footer image (excel_footer.png) centered
    const footerRowStart = worksheet.rowCount + 1;
    try {
      const excelFooterUrl = `${BASE_URL}/logo/excel-footer`;
      const footerResponse = await fetch(excelFooterUrl);
      if (footerResponse.ok) {
        const footerBlob = await footerResponse.blob();
        const footerBuffer = await footerBlob.arrayBuffer();
        const footerImageId = workbook.addImage({
          buffer: footerBuffer,
          extension: 'png',
        });
        
        // Add footer image centered - spanning columns B to I (columns 1-8)
        // Position it to be centered horizontally
        worksheet.addImage(footerImageId, {
          tl: { col: 2, row: footerRowStart }, // Start from column B (index 1)
          ext: { width: 700, height: 150 } // Adjust width and height as needed
        });
        console.log('Excel footer image added successfully');
        
        // Add empty rows to make space for the footer image
        for (let i = 0; i < 5; i++) {
          worksheet.addRow(emptyRow);
        }
      }
    } catch (error) {
      console.error('Failed to load excel footer image:', error);
      // Continue without footer image if it fails
    }
    
    // Set column widths conditionally based on level
    worksheet.columns = isLevelA ? [
      { width: 15 },  // Startzeit
      { width: 15 },  // Raum
      { width: 15 },  // PTN-Nummer
      { width: 20 },  // Nachname
      { width: 20 },  // Vorname
      { width: 15 },  // Geburtsdatum
      { width: 20 },  // Geburtsort
      { width: 10 },  // Level
      { width: 15 },  // Ort
      { width: 10 }   // Round
    ] : [
      { width: 20 },  // Vorbereitungszeit
      { width: 15 },  // Startzeit
      { width: 15 },  // Raum
      { width: 15 },  // PTN-Nummer
      { width: 20 },  // Nachname
      { width: 20 },  // Vorname
      { width: 15 },  // Geburtsdatum
      { width: 20 },  // Geburtsort
      { width: 10 },  // Level
      { width: 15 },  // Ort
      { width: 10 }   // Round
    ];
    
    // Generate and download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const filename = `sprechen_level_assignments${examName}.xlsx`;
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleDownloadPDFByModuleRoom = async () => {
    if (!examId) {
      alert('No exam selected');
      return;
    }

    try {
      // First, get the list of available PDFs
      const listResponse = await fetch(`${BASEURL}/list-attendance-pdfs?exam_id=${examId}`);
      
      if (!listResponse.ok) {
        const errorData = await listResponse.json();
        throw new Error(errorData.error || 'Failed to get PDF list');
      }

      const pdfData = await listResponse.json();
      const { pdfs } = pdfData;

      if (!pdfs || pdfs.length === 0) {
        alert('No PDFs available for this exam');
        return;
      }

      // Download each PDF individually
      for (const pdf of pdfs) {
        try {
          // Include start_time to get correct time slot
          const downloadUrl = `${BASEURL}/download-attendance-pdf?exam_id=${examId}&module=${encodeURIComponent(pdf.module)}&room=${encodeURIComponent(pdf.room)}${pdf.start_time ? `&start_time=${encodeURIComponent(pdf.start_time)}` : ''}`;
          const downloadResponse = await fetch(downloadUrl);
          
          if (!downloadResponse.ok) {
            console.error(`Failed to download ${pdf.filename}`);
            continue;
          }

          // Create blob and download
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = pdf.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          // Small delay between downloads to avoid overwhelming the browser
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (pdfError) {
          console.error(`Error downloading ${pdf.filename}:`, pdfError);
        }
      }

      alert(`Downloaded ${pdfs.length} attendance list PDFs`);
    } catch (error) {
      console.error('Error downloading PDFs:', error);
      alert(`Error downloading PDFs: ${error.message}`);
    }
  };

  // Filter and sort assignments based on email status
  const getFilteredAssignments = () => {
    if (!filterByUnsentEmails) {
      return assignments;
    }
    
    // Sort by invitation_sent status: unsent first, then sent
    return [...assignments].sort((a, b) => {
      if (a.invitation_sent === b.invitation_sent) {
        return 0; // Keep original order if both have same status
      }
      return a.invitation_sent ? 1 : -1; // Unsent emails first
    });
  };

  const filteredAssignments = getFilteredAssignments();

  const groupedData = filteredAssignments.reduce((acc, item) => {
    if (!acc[item.room]) acc[item.room] = [];
    acc[item.room].push(item);
    return acc;
  }, {});

  const handleSendAllEmails = async () => {
    if (!examId) return;
    setMassEmailStatus({sending: true, sent: 0, errors: []});
    try {
      const res = await fetch(`${BASEURL}/send-invitations-mass?exam_id=${examId}`, {
        method: 'POST',
      });
      const data = await res.json();
      setMassEmailStatus({sending: false, sent: data.sent, errors: data.errors || []});
      // Optionally, refresh assignments to update invitation_sent status
      fetchAssignments(examId);
    } catch (err) {
      setMassEmailStatus({sending: false, sent: 0, errors: [{error: 'Request failed'}]});
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: 'auto' }}>
      <h2>Seating Assignments</h2>
<button
  style={{ float: 'right' }}
  onClick={() => {
    setShowDialog(true);
    setFirstName('');
    setLastName('');
    setPtnNumber('');
    setNonModular(false);
    setSelectedModule('');
  }}
>
  ➕ Add Student
</button>
<button 
  onClick={handleGenerateAllPDFs} 
  style={{ marginLeft: '10px' }}
  disabled={isGenerating}
>
  {isGenerating ? (
    <>
      <Spinner />
      {asyncProgress > 0 ? `Generating... ${asyncProgress}%` : 'Starting...'}
    </>
  ) : 'Generate All PDFs (Fast & Async)'}
</button>
{isGenerating && asyncProgress > 0 && (
  <div style={{
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3',
    borderRadius: '4px',
    fontSize: '14px'
  }}>
    <div style={{ marginBottom: '5px' }}>
      <strong>Progress:</strong> {asyncProgress}% complete
    </div>
    <div style={{
      width: '100%',
      height: '20px',
      backgroundColor: '#e0e0e0',
      borderRadius: '10px',
      overflow: 'hidden'
    }}>
      <div style={{
        width: `${asyncProgress}%`,
        height: '100%',
        backgroundColor: '#4caf50',
        transition: 'width 0.3s ease'
      }} />
    </div>
    <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
      Status: {asyncStatus || 'processing'}
    </div>
  </div>
)}
<button
  onClick={resetPDFGenerationState}
  style={{ marginLeft: '10px' }}
  disabled={!isGenerating}
>
  Reset Generation State
</button>

{isStateRestored && (
  <div style={{ 
    marginTop: '10px', 
    padding: '8px', 
    backgroundColor: '#fff3cd', 
    border: '1px solid #ffeaa7', 
    borderRadius: '4px',
    fontSize: '14px',
    color: '#856404'
  }}>
    ⚠️ PDF generation state was restored from previous session. 
    If generation appears stuck, use the Reset button above.
  </div>
)}

{/* Save Options Dialog */}
{showSaveDialog && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      maxWidth: '400px',
      width: '90%',
      textAlign: 'center'
    }}>
      <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
        Choose Save Location
      </h3>
      
      <p style={{ marginBottom: '25px', color: '#666' }}>
        Where would you like to save the generated files?
      </p>
      
      {/* Filename preview */}
      {(() => {
        const firstAssignment = assignments[0];
        let examDate = '';
        if (firstAssignment && firstAssignment.start_time) {
          try {
            const date = new Date(firstAssignment.start_time);
            if (!isNaN(date.getTime())) {
              examDate = date.toLocaleDateString('de-DE');
            }
          } catch (error) {
            console.error('Error parsing date:', error);
          }
        }
        const filename = examDate ? `Exam ${examDate}.pdf` : `exam_${examId}_students.pdf`;
        
        return (
          <div style={{
            marginBottom: '20px',
            padding: '10px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#495057'
          }}>
            📄 <strong>Filename:</strong> {filename}
          </div>
        );
      })()}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <button
          onClick={handleSaveLocally}
          disabled={isGenerating}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            opacity: isGenerating ? 0.7 : 1,
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => {
            if (!isGenerating) {
              e.target.style.backgroundColor = '#0056b3';
            }
          }}
          onMouseOut={(e) => {
            if (!isGenerating) {
              e.target.style.backgroundColor = '#007bff';
            }
          }}
        >
          {isGenerating ? '⏳ Generating...' : '💾 Save Locally'}
        </button>
        
        <button
          onClick={handleSaveToGDrive}
          disabled={isGenerating || gdriveUploadStatus.uploading}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (isGenerating || gdriveUploadStatus.uploading) ? 'not-allowed' : 'pointer',
            opacity: (isGenerating || gdriveUploadStatus.uploading) ? 0.7 : 1,
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => {
            if (!isGenerating && !gdriveUploadStatus.uploading) {
              e.target.style.backgroundColor = '#1e7e34';
            }
          }}
          onMouseOut={(e) => {
            if (!isGenerating && !gdriveUploadStatus.uploading) {
              e.target.style.backgroundColor = '#28a745';
            }
          }}
        >
          {isGenerating ? '⏳ Generating...' : 
           gdriveUploadStatus.uploading ? '⏳ Uploading...' : '☁️ Save to Google Drive'}
        </button>
        
        <button
          onClick={() => {
            setShowSaveDialog(false);
            setGdriveUploadStatus({ uploading: false, message: '' });
            setIsGenerating(false);
          }}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#545b62'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Cancel
        </button>
      </div>
      
      {gdriveUploadStatus.message && (
        <div style={{
          marginTop: '20px',
          padding: '10px',
          backgroundColor: gdriveUploadStatus.message.includes('Successfully') ? '#d4edda' : '#f8d7da',
          border: `1px solid ${gdriveUploadStatus.message.includes('Successfully') ? '#c3e6cb' : '#f5c6cb'}`,
          borderRadius: '4px',
          color: gdriveUploadStatus.message.includes('Successfully') ? '#155724' : '#721c24',
          fontSize: '14px'
        }}>
          {gdriveUploadStatus.message}
        </div>
      )}
      
      {isGenerating && (
        <div style={{
          marginTop: '20px',
          padding: '10px',
          backgroundColor: '#cce5ff',
          border: '1px solid #b3d9ff',
          borderRadius: '4px',
          color: '#004085',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          ⏳ Generating PDF... This may take a few moments for large files.
        </div>
      )}
    </div>
  </div>
)}

      {/* Main Action Buttons */}
      <div style={{ 
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '16px' }}>📊 Export & Download</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
          <button 
            onClick={handleDownloadAllExamFiles}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
          >
            🚀 Download All Exam Files
          </button>
          
          <button 
            onClick={handleDownloadAll}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
          >
            📥 Download All as Excel
          </button>
          
          {/* <button 
            onClick={handleDownloadPerRoom}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a32a3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6f42c1'}
          >
            🏠 Download by Room
          </button> */}
          
          {/* <button 
            onClick={handleDownloadPerModule}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              backgroundColor: '#fd7e14',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#e8690b'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#fd7e14'}
          >
            📚 Download by Module & Room
          </button> */}
          
          <button 
            onClick={handleDownloadSprechenLevel}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#138496'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#17a2b8'}
          >
            🗣️ Download Sprechen/Mundlich Format
          </button>
          
          <button 
            onClick={handleDownloadPDFByModuleRoom}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
          >
            📄 Download By Module & Room (as PDFs)
          </button>
        </div>

        <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '16px' }}>📧 Email Management</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
          <button
            onClick={handleSendAllEmails}
            disabled={massEmailStatus.sending}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              backgroundColor: massEmailStatus.sending ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: massEmailStatus.sending ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500',
              opacity: massEmailStatus.sending ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!massEmailStatus.sending) {
                e.target.style.backgroundColor = '#1e7e34';
              }
            }}
            onMouseOut={(e) => {
              if (!massEmailStatus.sending) {
                e.target.style.backgroundColor = '#28a745';
              }
            }}
          >
            {massEmailStatus.sending ? '⏳ Sending...' : '📧 Send All Emails'}
          </button>
          
          {massEmailStatus.sent > 0 && (
            <span style={{ 
              padding: '8px 12px', 
              backgroundColor: '#d4edda', 
              color: '#155724', 
              borderRadius: '6px', 
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #c3e6cb'
            }}>
              ✅ Sent: {massEmailStatus.sent}
            </span>
          )}
          
          {massEmailStatus.errors.length > 0 && (
            <span style={{ 
              padding: '8px 12px', 
              backgroundColor: '#f8d7da', 
              color: '#721c24', 
              borderRadius: '6px', 
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #f5c6cb'
            }}>
              ❌ Errors: {massEmailStatus.errors.length}
            </span>
          )}
        </div>

        <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '16px' }}>⚙️ View Options</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            padding: '8px 12px',
            backgroundColor: groupByRoom ? '#e3f2fd' : 'transparent',
            borderRadius: '6px',
            border: groupByRoom ? '1px solid #2196f3' : '1px solid #dee2e6',
            transition: 'all 0.2s ease'
          }}>
            <input
              type="checkbox"
              checked={groupByRoom}
              onChange={() => setGroupByRoom(!groupByRoom)}
              style={{ marginRight: '8px' }}
            />
            <span style={{ 
              color: groupByRoom ? '#1976d2' : '#6c757d',
              fontWeight: groupByRoom ? '500' : '400'
            }}>
              🏠 Group by Room
            </span>
          </label>
          
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            padding: '8px 12px',
            backgroundColor: filterByUnsentEmails ? '#fff3cd' : 'transparent',
            borderRadius: '6px',
            border: filterByUnsentEmails ? '1px solid #ffc107' : '1px solid #dee2e6',
            transition: 'all 0.2s ease'
          }}>
            <input
              type="checkbox"
              checked={filterByUnsentEmails}
              onChange={() => setFilterByUnsentEmails(!filterByUnsentEmails)}
              style={{ marginRight: '8px' }}
            />
            <span style={{ 
              color: filterByUnsentEmails ? '#856404' : '#6c757d',
              fontWeight: filterByUnsentEmails ? '500' : '400'
            }}>
              🔍 Filter Unsent Emails
            </span>
          </label>
        </div>
      </div>
      
      {/* Email Status Summary */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '15px', 
        backgroundColor: filterByUnsentEmails ? '#fff3cd' : '#f8f9fa', 
        borderRadius: '8px',
        border: filterByUnsentEmails ? '2px solid #ffc107' : '1px solid #dee2e6',
        transition: 'all 0.2s ease'
      }}>
        <h4 style={{ 
          margin: '0 0 15px 0', 
          color: filterByUnsentEmails ? '#856404' : '#495057', 
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📊 Email Status Summary
          {filterByUnsentEmails && (
            <span style={{ 
              fontSize: '12px', 
              backgroundColor: '#ffc107', 
              color: '#856404',
              padding: '4px 8px',
              borderRadius: '12px',
              fontWeight: '500'
            }}>
              🔍 Filter Active
            </span>
          )}
        </h4>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
          <span style={{ 
            padding: '8px 12px', 
            backgroundColor: '#d4edda', 
            color: '#155724', 
            borderRadius: '6px', 
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #c3e6cb',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            ✅ Sent: {(() => {
              const uniqueStudents = new Set();
              assignments.forEach(a => {
                if (a.invitation_sent) {
                  uniqueStudents.add(a.student_id);
                }
              });
              return uniqueStudents.size;
            })()}
          </span>
          
          <span style={{ 
            padding: '8px 12px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '6px', 
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #f5c6cb',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            ❌ Not Sent: {(() => {
              const uniqueStudents = new Set();
              assignments.forEach(a => {
                if (!a.invitation_sent) {
                  uniqueStudents.add(a.student_id);
                }
              });
              return uniqueStudents.size;
            })()}
          </span>
          
          <span style={{ 
            padding: '8px 12px', 
            backgroundColor: '#e2e3e5', 
            color: '#383d41', 
            borderRadius: '6px', 
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #d6d8db',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            📊 Total Students: {(() => {
              const uniqueStudents = new Set();
              assignments.forEach(a => {
                uniqueStudents.add(a.student_id);
              });
              return uniqueStudents.size;
            })()}
          </span>
          
          {filterByUnsentEmails && (
            <span style={{ 
              padding: '8px 12px', 
              backgroundColor: '#fff3cd', 
              color: '#856404', 
              borderRadius: '6px', 
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #ffeaa7',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              🔍 Showing: {filteredAssignments.length} items
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : groupByRoom ? (
        Object.entries(groupedData).map(([room, roomAssignments]) => (
          <div key={room} style={{ marginBottom: '40px' }}>
            <h3>Room: {room}</h3>
            <table border="1" cellPadding="10">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Personal ID</th>
                  <th>Room</th>
                  <th>Module</th>
                  <th>Start Time</th>
                  <th>Preparation Time</th>
                  <th>Duration (min)</th>
                  <th>Email Status</th>
                  <th>Generate PDF</th>
                  <th>Send Email</th>
                </tr>
              </thead>
<tbody>
  {roomAssignments.map((a, idx) => (
    <tr key={a.assignment_id}>
      <td>{idx + 1}</td>
      <td>{`${a.first_name} ${a.last_name}`}</td>
      <td>
        {editingId === a.assignment_id ? (
          <>
            <input
              value={newPtnNumber}
              onChange={(e) => setNewPtnNumber(e.target.value)}
              style={{ width: '120px' }}
            />
            <button onClick={() => handleUpdatePtnNumber(a.student_id, a.assignment_id)}>Save</button>
            <button onClick={() => setEditingId(null)}>Cancel</button>
          </>
        ) : (
          <>
            {a.ptn_number || '-'}
            <button onClick={() => {
              setEditingId(a.assignment_id);
              setNewPtnNumber(a.ptn_number || '');
            }} style={{ marginLeft: '10px' }}>Edit</button>
          </>
        )}
      </td>
      <td>{a.room}</td>
      <td>{a.module}</td>
      <td>{formatDateTimeDDMMYYYY(a.start_time)}</td>
      <td>{a.preparation_time ? formatDateTimeDDMMYYYY(a.preparation_time) : '-'}</td>
      <td>{a.duration}</td>
      <td>
        <span style={{ 
          color: a.invitation_sent ? 'green' : 'red',
          fontWeight: 'bold'
        }}>
          {a.invitation_sent ? '✓ Sent' : '✗ Not Sent'}
        </span>
      </td>
      <td>
        <button onClick={() => handleGeneratePDF(a.student_id)}>Generate PDF</button>
      </td>
      <td>
        <button
          onClick={() => handleSendEmail(a.student_id)}
          disabled={emailStatus[a.student_id] === 'sending' || a.invitation_sent}
        >
          {a.invitation_sent ? 'Sent!' : emailStatus[a.student_id] === 'sent' ? 'Sent!' : emailStatus[a.student_id] === 'sending' ? 'Sending...' : 'Send Email'}
        </button>
      </td>
    </tr>
  ))}
</tbody>

            </table>
          </div>
        ))
      ) : (
        <table border="1" cellPadding="10">
          <thead>
            <tr>
              <th>#</th>
              <th>Student</th>
              <th>PTN Number</th>
              <th>Room</th>
              <th>Module</th>
              <th>Start Time</th>
              <th>Preparation Time</th>
              <th>Duration (min)</th>
              <th>Email Status</th>
              <th>Generate PDF</th>
              <th>Send Email</th>
            </tr>
          </thead>
<tbody>
  {filteredAssignments.map((a, idx) => (
    <tr key={a.assignment_id}>
      <td>{idx + 1}</td>
      <td>{`${a.first_name} ${a.last_name}`}</td>
      <td>
        {editingId === a.assignment_id ? (
          <>
            <input
              value={newPtnNumber}
              onChange={(e) => setNewPtnNumber(e.target.value)}
              style={{ width: '120px' }}
            />
            <button onClick={() => handleUpdatePtnNumber(a.student_id, a.assignment_id)}>Save</button>
            <button onClick={() => setEditingId(null)}>Cancel</button>
          </>
        ) : (
          <>
            {a.ptn_number || '-'}
            <button onClick={() => {
              setEditingId(a.assignment_id);
              setNewPtnNumber(a.ptn_number || '');
            }} style={{ marginLeft: '10px' }}>Edit</button>
          </>
        )}
      </td>
      <td>{a.room}</td>
      <td>{a.module}</td>
      <td>{formatDateTimeDDMMYYYY(a.start_time)}</td>
      <td>{a.preparation_time ? formatDateTimeDDMMYYYY(a.preparation_time) : '-'}</td>
      <td>{a.duration}</td>
      <td>
        <span style={{ 
          color: a.invitation_sent ? 'green' : 'red',
          fontWeight: 'bold'
        }}>
          {a.invitation_sent ? '✓ Sent' : '✗ Not Sent'}
        </span>
      </td>
      <td>
        <button onClick={() => handleGeneratePDF(a.student_id)}>Generate PDF</button>
      </td>
      <td>
        <button
          onClick={() => handleSendEmail(a.student_id)}
          disabled={emailStatus[a.student_id] === 'sending' || a.invitation_sent}
        >
          {a.invitation_sent ? 'Sent!' : emailStatus[a.student_id] === 'sent' ? 'Sent!' : emailStatus[a.student_id] === 'sending' ? 'Sending...' : 'Send Email'}
        </button>
      </td>
    </tr>
  ))}
</tbody>

        </table>
      )}
      {showDialog && (
  <div style={{
    position: 'fixed', top: '20%', left: '35%',
    background: 'white', padding: '20px', border: '1px solid #ccc', zIndex: 1000
  }}>
    <h3>Add Student to Exam</h3>
    <input placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
    <br />
    <input placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
    <br />
    <input placeholder="PTN Number" value={ptnNumber} onChange={(e) => setPtnNumber(e.target.value)} />
    <br />
    <label>
      <input
        type="checkbox"
        checked={nonModular}
        onChange={() => setNonModular(!nonModular)}
      /> Non Modular
    </label>
{!nonModular && modules.length > 0 && (
  <select
    value={selectedModule}
    onChange={(e) => setSelectedModule(e.target.value)}
  >
    <option value="">-- Select Module --</option>
    {modules.map(mod => (
      <option key={mod.id} value={mod.id}>{mod.name}</option>
    ))}
  </select>
)}

    <br /><br />
    <button onClick={handleAddStudent}>Submit</button>
    <button onClick={() => setShowDialog(false)} style={{ marginLeft: '10px' }}>Cancel</button>
  </div>
)}

    </div>
  );
};

export default SeatingAssignmentsPage;
